import { useEffect, useMemo, useState } from 'react';
import { DateRangePicker, RangeKeyDict, createStaticRanges } from 'react-date-range';

import ButtonDefault from 'app/components/Button/ButtonDefault';
import { ModalDatePicker } from 'app/components/Filter/InputDateFilter';

import {
  ExportSectionWrapper,
  FooterContainer,
} from 'app/modules/reporting/components/ReportTimeframe';
import {
  CUSTOM_STATIC_RANGES,
  INITIAL_FILTER_VALUE,
  RANGES,
} from 'app/modules/reporting/core/constants';

import { addMonths, endOfDay, isBefore, isSameDay, startOfDay } from 'date-fns';

export interface TimeframeFilter {
  fromDate: Date;
  toDate: Date;
}

interface TimeframeExportProps {
  onExport: (filter: TimeframeFilter) => void;
  onClose: () => void;
  isSubmitting: boolean;
  title?: string;
  submitBtnExtraStyle?: React.CSSProperties;
}

export const TimeframeExport: React.FC<TimeframeExportProps> = ({
  onExport,
  onClose,
  isSubmitting,
  title = 'Export',
  submitBtnExtraStyle,
}) => {
  const [filter, setFilter] = useState<TimeframeFilter>(INITIAL_FILTER_VALUE);
  const [firstSelectedDay, setFirstSelectedDay] = useState<Date | null>(null);

  useEffect(() => {
    return () => setFilter(INITIAL_FILTER_VALUE);
  }, []);

  useEffect(() => {
    if (isSameDay(filter.fromDate, filter.toDate)) setFirstSelectedDay(filter.fromDate);

    if (
      firstSelectedDay &&
      ![filter.fromDate, filter.toDate].some((date) =>
        isSameDay(startOfDay(date), startOfDay(firstSelectedDay)),
      )
    ) {
      setFirstSelectedDay(null);
    }
  }, [filter, firstSelectedDay]);

  const toLocalDate = (date: Date): Date =>
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );

  const handleDateChange = () => (rangesByKey: RangeKeyDict) => {
    const range = rangesByKey.selection as { startDate: Date; endDate: Date };
    if (!range) return;

    const localStartDate = toLocalDate(range.startDate);
    const localEndDate = toLocalDate(range.endDate);

    const startDate = startOfDay(localStartDate);
    let endDate = endOfDay(localEndDate);

    const isSameDaySelected = isSameDay(startDate, endDate);

    if (isSameDaySelected) {
      const adjusted = endOfDay(startDate);
      setFilter({ fromDate: adjusted, toDate: adjusted });
      return;
    }

    const maxEndDate = endOfDay(addMonths(startDate, 1));
    if (isBefore(maxEndDate, endDate)) endDate = maxEndDate;

    setFilter({ fromDate: startDate, toDate: endDate });
  };

  const handleCancel = () => {
    setFilter(INITIAL_FILTER_VALUE);
    onClose?.();
  };

  const handleExport = () => onExport(filter);

  const staticRanges = useMemo(() => createStaticRanges(CUSTOM_STATIC_RANGES), []);

  return (
    <ModalDatePicker>
      <DateRangePicker
        onChange={handleDateChange()}
        showPreview
        months={2}
        staticRanges={staticRanges}
        ranges={RANGES(filter)}
        direction="horizontal"
        rangeColors={['var(--primary)']}
        maxDate={endOfDay(new Date())}
        footerContent={
          <FooterContainer>
            <ExportSectionWrapper>
              <ButtonDefault
                theme="btn-light"
                onClick={handleCancel}
                style={{ width: 160, marginRight: 12 }}
                className="d-flex align-items-center justify-content-center"
              >
                Cancel
              </ButtonDefault>
              <ButtonDefault
                onClick={handleExport}
                isSubmitting={isSubmitting}
                disabled={isSubmitting}
                style={{ minWidth: 160, borderRadius: 8, ...submitBtnExtraStyle }}
              >
                {title}
              </ButtonDefault>
            </ExportSectionWrapper>
          </FooterContainer>
        }
      />
    </ModalDatePicker>
  );
};
